import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Heart } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEMO_KEY = 'tla_demo_favorites';

function isDemoListing(listingId) {
  return typeof listingId === 'string' && listingId.startsWith('sample-');
}

function readDemoFavorites() {
  try {
    return JSON.parse(localStorage.getItem(DEMO_KEY) || '[]');
  } catch {
    return [];
  }
}

function writeDemoFavorites(list) {
  try {
    localStorage.setItem(DEMO_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

export default function FavoriteButton({ listingId, listingTitle, listing, size = 'default', showText = true }) {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);
  const demo = isDemoListing(listingId);

  useEffect(() => {
    if (demo) {
      const demos = readDemoFavorites();
      setIsFavorite(demos.some(f => f.listing_id === listingId));
    } else if (user && listingId) {
      checkFavoriteStatus();
    }
  }, [user, listingId]);

  const checkFavoriteStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/favorites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const favList = Array.isArray(data) ? data : (data.favorites || []);
        const isFav = favList.some(f => (f.listing?.id || f.listing_id) === listingId);
        setIsFavorite(isFav);
      }
    } catch (error) {
      console.error('Failed to check favorite status:', error);
    }
  };

  const toggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // ── Demo / sample listing path (localStorage, no auth required) ──────────
    if (demo) {
      const demos = readDemoFavorites();
      if (isFavorite) {
        const updated = demos.filter(f => f.listing_id !== listingId);
        const ok = writeDemoFavorites(updated);
        if (ok) {
          setIsFavorite(false);
          window.dispatchEvent(new Event('favorites-updated'));
          toast.success(t('favorites.demo_removed', 'Removed preview property'));
        } else {
          toast.error(t('favorites.remove_error', 'Failed to remove favorite'));
        }
      } else {
        const entry = {
          listing_id: listingId,
          listing_title: listing?.title || listingTitle || '',
          listing_photo: listing?.hero_photo_url || '',
          listing_location: listing?.location_name || '',
          listing_price: listing?.base_amount || listing?.total_monthly_cost || 0,
          is_demo: true,
          created_at: new Date().toISOString(),
        };
        const ok = writeDemoFavorites([...demos, entry]);
        if (ok) {
          setIsFavorite(true);
          window.dispatchEvent(new Event('favorites-updated'));
          toast.success(t('favorites.demo_saved', 'Saved preview property'));
        } else {
          toast.error(t('favorites.save_failed', 'Favorite save failed'));
        }
      }
      return;
    }

    // ── Real backend listing path ─────────────────────────────────────────────
    if (!user) {
      toast.error(t('favorites.login_required', 'Please login to save favorites'));
      return;
    }

    setLoading(true);
    try {
      if (isFavorite) {
        const response = await fetch(`${API_URL}/api/favorites/${encodeURIComponent(listingId)}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          setIsFavorite(false);
          window.dispatchEvent(new Event('favorites-updated'));
          toast.success(t('favorites.removed', 'Removed from favorites'));
        } else {
          let detail = '';
          try { detail = (await response.json()).detail || ''; } catch {}
          console.warn(`DELETE /favorites/${listingId} → ${response.status} ${detail}`);
          toast.error(detail || t('favorites.remove_error', 'Failed to remove favorite'));
        }
      } else {
        const response = await fetch(`${API_URL}/api/favorites/${encodeURIComponent(listingId)}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          setIsFavorite(true);
          window.dispatchEvent(new Event('favorites-updated'));
          toast.success(t('favorites.saved_toast', 'Saved to favorites!'));
        } else {
          let detail = '';
          try { detail = (await response.json()).detail || ''; } catch {}
          console.warn(`POST /favorites/${listingId} → ${response.status} ${detail}`);
          toast.error(detail || t('favorites.save_failed', 'Favorite save failed'));
        }
      }
    } catch (error) {
      toast.error(t('favorites.save_failed', 'Favorite save failed'));
    } finally {
      setLoading(false);
    }
  };

  const buttonClasses = size === 'icon'
    ? 'w-10 h-10 rounded-full'
    : '';

  return (
    <Button
      variant="ghost"
      size={size}
      onClick={toggleFavorite}
      disabled={loading}
      className={`${buttonClasses} ${
        isFavorite
          ? 'text-red-500 hover:text-red-400 hover:bg-red-500/10'
          : 'text-slate-600 hover:text-red-400 hover:bg-slate-800'
      }`}
      data-testid="favorite-button"
    >
      <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
      {showText && (
        <span className="ml-2">{isFavorite ? t('favorites.saved', 'Saved') : t('favorites.save', 'Save')}</span>
      )}
    </Button>
  );
}
